// Admission ticket as the FIRST signalling message instead of in the URL (P-39).
//
// The URL form (wss://host/?...&ticket=<jwt>) puts the ticket in every load
// balancer and proxy access log on the way. In first-message mode the URL has
// no ticket and, once the socket opens, the player sends
//
//     {"type":"admission","ticket":"<jwt>"}
//
// before anything else; signalling closes with 4401 "admission required" when
// it is missing or invalid.
//
// How: Epic's WebSocketTransport is ONE object for the life of the player — the
// lib's own reconnect and App.js's in-session reconnect
// (webRtcPlayer.connectToSignallingServer) both call transport.connect() again,
// which opens a new WebSocket whose onopen calls `this.handleOnOpen()`. Wrapping
// handleOnOpen on the instance therefore sends the admission message first on
// EVERY socket this player opens, ahead of the lib's own 'open' listener (which
// sends listStreamers) and the keepalive ping. The message goes straight to the
// transport, not through SignallingProtocol.sendMessage, which would copy the
// ticket into the debug log and the 'out' event.
//
// A 4401 close turns the lib's automatic reconnect off before the lib's close
// listener decides whether to reconnect: the same ticket would be refused again.
//
// CommonJS and dependency-free so plain node can test it (test/admission.js).

const ADMISSION_MESSAGE_TYPE = 'admission';
const ADMISSION_REQUIRED_CODE = 4401;

function admissionMessage(ticket) {
  return JSON.stringify({ type: ADMISSION_MESSAGE_TYPE, ticket: String(ticket || '') });
}

// `controller`: the lib's WebRtcPlayerController (pixelStreaming._webRtcController).
// Returns false (and changes nothing) when the transport is not the expected shape.
function installAdmission(controller, ticket) {
  const transport = controller && (controller.transport || (controller.protocol && controller.protocol.transport));
  if (!transport || typeof transport.handleOnOpen !== 'function' || typeof transport.sendMessage !== 'function') {
    return false;
  }
  if (transport.__spxAdmissionInstalled) return true;
  transport.__spxAdmissionInstalled = true;

  const message = admissionMessage(ticket);
  const originalOpen = transport.handleOnOpen;
  transport.handleOnOpen = function handleOnOpenWithAdmission(...args) {
    try {
      transport.sendMessage(message);
    } catch (e) {
      // A socket that closed between open and here: its close event follows.
    }
    return originalOpen.apply(transport, args);
  };

  if (typeof transport.handleOnClose === 'function') {
    const originalClose = transport.handleOnClose;
    transport.handleOnClose = function handleOnCloseWithAdmission(event, ...rest) {
      if (event && Number(event.code) === ADMISSION_REQUIRED_CODE) {
        controller.enableAutoReconnect = false;
        controller.forceReconnect = false;
      }
      return originalClose.call(transport, event, ...rest);
    };
  }
  return true;
}

// The signalling URL's query string, with or without the ticket.
function signallingQuery(params, { ticket, ticketInUrl = true } = {}) {
  const parts = Object.keys(params).map((k) => `${k}=${params[k]}`);
  if (ticketInUrl) parts.push(`ticket=${encodeURIComponent(ticket)}`);
  return parts.join('&');
}

module.exports = {
  ADMISSION_MESSAGE_TYPE,
  ADMISSION_REQUIRED_CODE,
  admissionMessage,
  installAdmission,
  signallingQuery,
};
